import styled from "@emotion/styled";
import {
  Button,
  CircularProgress,
  LinearProgress,
  Pagination,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import AttributeSearchInput from "../../components/AttributeSearchInput";
import { COLOR_CONSTANTS } from "../../helpers/Colors";
import {
  acknowledgePO,
  getCities,
  getOrderList,
  getPODoc,
  sendEmailReminder,
  uploadInvoice,
  uploadPayments,
} from "../../services/invoiceService";
import {
  FINS_ONLY_PO_STATUSES,
  PO_STATUS_SIDEBAR_ORDER,
  VENDOR_ACTION_PO_STATUSES,
} from "./constants/statusMapping";
import LoaderV2 from "./components/LoaderV2";
import OrderFilterCard from "./components/OrderFilter";
import StatusChipStrip from "./components/StatusChipStrip";
import StatusFilter from "./components/StatusFilter";
import DataTable from "./components/OrderTable";
import UploadFile from "./components/UploadFile";
import MobilePagination from "../../components/mobile/MobilePagination";
import { MOBILE_FAB_SX } from "./components/mobileFabSx";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useInvoiceV2Flag } from "./hooks/useInvoiceV2Flag";
import { filterData } from "./constants/filters";
import { getPoDestination } from "./utils/poRouting";
import { useToast } from "./hooks/useToast";
import { UserContext } from "../../context/UserContext";
import { getUserFactories } from "../../services/user";
import { WithPermission } from "./components/WithPermission";
import { BOUNCER_PERMISSIONS } from "../../constants/bouncerPermissions";
import ViewPOModal from "./components/ViewPO";

const FlexBox = styled(Box)`
  display: flex;
  align-items: center;
`;

const PAGE_SIZE = 10;

function OrderDetails({
  hideInvoiceUpload = false,
  hideInvoiceExpand = false,
  hidePaymentUpload = false,
}) {
  const isMobileViewport = useIsMobile();
  const { isV2 } = useInvoiceV2Flag();
  const isMobile = isV2 && isMobileViewport;
  // The v2 shell hides this when its Payments tab takes over uploads; the v1
  // shell passes nothing, so the entry point stays for everyone else.
  const showV1PaymentUpload = !hidePaymentUpload;
  const { invoiceFactory, user, setInvoiceFactory, isInternal } =
    React.useContext(UserContext);
  const [viewInvoice, setViewInvoice] = React.useState(null);
  const [actionStatus, setActionStatus] = React.useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const factoryItemCode = invoiceFactory?.item_code;
  const initialFactoryItemCodeRef = React.useRef(null);
  React.useEffect(() => {
    if (!factoryItemCode) return;
    if (initialFactoryItemCodeRef.current === null) {
      initialFactoryItemCodeRef.current = factoryItemCode;
      return;
    }
    if (initialFactoryItemCodeRef.current === factoryItemCode) return;
    initialFactoryItemCodeRef.current = factoryItemCode;
    setSearchParams((prev) => {
      prev.delete("filter_status");
      prev.delete("filterData");
      prev.delete("query");
      prev.delete("searchBy");
      prev.delete("page");
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [factoryItemCode]);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const page = parseInt(searchParams.get("page"), 10) || 1;
  const orderStatus = searchParams.get("filter_status") || "all_orders";
  const search = searchParams.get("query") || "";
  const searchBy = searchParams.get("searchBy") || "po_code";
  const isPendingAck = orderStatus === "pending_acknowledgement";
  const isVendorActionTab = VENDOR_ACTION_PO_STATUSES.has(orderStatus);
  const pendingAckAttributes = React.useMemo(
    () => [{ key: "po_code", label: "Order Code", placeholder: "Search by Order Code" }],
    []
  );

  const offset = PAGE_SIZE * (page - 1);
  const filterDataParam = searchParams.get("filterData");
  const filter = React.useMemo(() => {
    if (!filterDataParam) return {};
    try {
      return JSON.parse(filterDataParam);
    } catch {
      return {};
    }
  }, [filterDataParam]);
  const [selectedInvoice, setSelectedInvoice] = React.useState();
  const bulkSelection = React.useState([]);
  const [bulkSelectedItems, setBulkSelected] = bulkSelection;

  // Fetch factories associated with the user
  const {
    isLoading: isFactoryLoading,
    isError: isFactoryError,
    data: factoryList,
  } = useQuery(
    ["factoryListQuery"],
    () =>
      getUserFactories({ userId: user?.email }),
    {
      enabled: user?.id?.length > 1,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const {
    data: citiesData,
    isLoading: isCitiesLoading,
  } = useQuery(
    ["citiesQuery"],
    getCities,
    {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  function getFactorycodeForPOList() {
    const hasItemCode = invoiceFactory && invoiceFactory.item_code;
    const hasAllFactories = factoryList?.all_factories;

    if (hasItemCode) {
      if (invoiceFactory.item_code === "all") {
        if (hasAllFactories) return "";
        return getFactoriesFromFactoryMap(factoryList?.factory_map);
      }
      return invoiceFactory.item_code;
    } else if (hasAllFactories) {
      const _factory = {
        item_code: "all",
        item_name: "All factories",
        type: "FCT",
      };
      setInvoiceFactory(_factory);
      localStorage.setItem("invoiceFactory", JSON.stringify(_factory));
      return "";
    }

    let _factory = {};

    if (Object.values(factoryList?.factory_map).length > 1) {
      _factory = {
        item_code: "all",
        item_name: "All factories",
        type: "FCT",
      };
      setInvoiceFactory(_factory);
    } else {
      const factoryCode = Object.keys(factoryList?.factory_map);
      _factory = {
        item_code: factoryCode,
        item_name: factoryList?.factory_map[factoryCode],
        type: "FCT",
      };
      setInvoiceFactory(_factory);
    }
    localStorage.setItem("invoiceFactory", JSON.stringify(_factory));
    return getFactoriesFromFactoryMap(factoryList?.factory_map);
  }

  const {
    isLoading,
    isFetching,
    isError,
    data: orderList,
    error,
  } = useQuery(
    ["orderListQuery", { offset, filter, search, searchBy, orderStatus, invoiceFactory }],
    () =>
      getOrderList({
        offset,
        limit: 10,
        filter,
        search,
        searchBy,
        orderStatus,
        factoryCode: getFactorycodeForPOList(),
      }),
    {
      retry: false,
      enabled: offset != null && !isFactoryLoading && !isFactoryError,
      keepPreviousData: true,
    }
  );

  const showFinsOnly = React.useMemo(() => {
    if (isInternal) return true;
    const counts = orderList?.count || {};
    const poList = orderList?.po_list || [];
    const hasFinsByCount = Object.entries(counts).some(
      ([k, v]) => FINS_ONLY_PO_STATUSES.has(k) && v > 0
    );
    const hasFinsByList = poList.some((po) => po?.source === "FINS");
    return hasFinsByCount || hasFinsByList;
  }, [orderList, isInternal]);

  const sidebarData = React.useMemo(() => {
    if (!orderList) return {};
    const counts = orderList.count || {};
    const data = {};
    for (const key of PO_STATUS_SIDEBAR_ORDER) {
      if (!showFinsOnly && FINS_ONLY_PO_STATUSES.has(key)) continue;
      data[key] = counts[key] ?? 0;
    }
    return data;
  }, [orderList, showFinsOnly]);

  const dynamicFilterData = React.useMemo(() => {
    const base = filterData(orderStatus === "all_orders" ? "all_orders" : "order");
    if (showFinsOnly) return base;
    return base.map((section) => {
      if (section.filter_key !== "po_status") return section;
      return {
        ...section,
        filters: section.filters.filter(
          (f) => !FINS_ONLY_PO_STATUSES.has(f.value)
        ),
      };
    });
  }, [orderStatus, showFinsOnly]);

  const activeFilterStatuses = React.useMemo(() => {
    const s = new Set();
    if (orderStatus && orderStatus !== "all_orders") s.add(orderStatus);
    if (Array.isArray(filter.po_status)) {
      filter.po_status.forEach((v) => s.add(v));
    }
    return s;
  }, [orderStatus, filter]);

  const { isLoading: isUploading, mutate: uploadInvoiceMutate } = useMutation(
    uploadInvoice,
    {
      // Navigate to success page
      onSuccess: (data) => {
        // Invalidate query
        showToast({ type: "success", message: "Invoice upload successfully" });
        queryClient.invalidateQueries({ queryKey: ["orderListQuery"] });
        // Navigate to post upload screen
        navigate(`/dashboard/invoice-management/post-upload`, {
          state: { data },
        });
      },

      onError: (error) => {
        showToast({ type: "error", message: "Error uploading invoice" });
      },
    }
  );

  const { isLoading: isPaymentUploading, mutate: uploadPaymentMutate } =
    useMutation(uploadPayments, {
      // Navigate to success page
      onSuccess: (data) => {
        // Invalidate query
        showToast({ type: "success", message: "Payments upload successfully" });
        queryClient.invalidateQueries({ queryKey: ["orderListQuery"] });
      },

      onError: (error) => {
        showToast({ type: "error", message: "Error uploading file" });
      },
    });

  const setCurrentPage = (page) => {
    setSearchParams((prev) => {
      prev.set("page", page);
      return prev;
    });
  };

  const { mutate: sendEmail } = useMutation({
    mutationFn: (data) => sendEmailReminder(data),
    // Navigate to success page
    onSuccess: (data, variable) => {
      // Invalidate query
      showToast({
        type: "success",
        message: "Mail has been sent to the vendor successfully",
      });
      setActionStatus({ ...actionStatus, [variable?.po_code]: false });
      queryClient.invalidateQueries({ queryKey: ["orderListQuery"] });
    },
    onError: (error, variable) => {
      setActionStatus({ ...actionStatus, [variable?.po_code]: false });
      showToast({
        type: "error",
        message: "Error sending email to the vendor",
      });
    },
  });

  const { mutate: getViewPODoc } = useMutation({
    mutationFn: (data) => {
      let source = "ims";
      if (data?.order_status === "pending_acknowledgement") {
        source = "fins";
      } else if (data?.source) {
        source = data?.source;
      }
      const fins_status =
        data?.metadata?.po_status === "revision_requested"
          ? "revised"
          : data?.fins_status;
      return getPODoc({
        po_code: data?.po_code,
        source,
        fins_status,
      });
    },
    onSuccess: (data, variable) => {
      setActionStatus({ ...actionStatus, [variable?.po_code]: false });
      setViewInvoice({
        ...variable,
        _file: {
          file_name: data.file_name,
          pre_signed_url: data.pre_signed_url,
        },
      });
    },
    onError: (error, variable) => {
      setActionStatus({ ...actionStatus, [variable?.po_code]: false });
      showToast({ type: "error", message: "Error getting the document" });
    },
  });

  const { mutate: downloadPO } = useMutation({
    mutationFn: async (data) => {
      let source = "ims";
      if (data?.order_status === "pending_acknowledgement") {
        source = "fins";
      } else if (data?.source) {
        source = data?.source;
      }
     const res = await getPODoc({
          po_code: data?.po_code,
        source,
        fins_status: data?.fins_status,
      })
      const link = document.createElement('a')
      link.href = res.pre_signed_url
      link.setAttribute('download', res.file_name)
      document.body.appendChild(link)
      link.click()
      link.remove()
      return;
    },
    // Navigate to success page
    onSuccess: (data, variable) => {
      // Invalidate query
      showToast({ type: "success", message: "Download started successfully" });
      setActionStatus({ ...actionStatus, [variable?.po_code]: false });
    },
    onError: (error, variable) => {
      setActionStatus({ ...actionStatus, [variable?.po_code]: false });
      showToast({ type: "error", message: "Error downloading document" });
    },
  });

  const resetSearchQuery = () => {
    setSearchParams((prev) => {
      prev.delete("query");
      prev.delete("searchBy");
      return prev;
    });
  };

  const resetAction = () => {
    setActionStatus({});
    queryClient.invalidateQueries({ queryKey: ["orderListQuery"] });
  };

  const { mutate: acknowledgePOs, isLoading: isApprovalPending } = useMutation({
    mutationFn: async (arg) => {
      const rows = arg?.data ?? [];
      if (rows.length === 0) {
        throw new Error("No POs selected");
      }
      const payload = rows
        .filter((row) => row.po_code)
        .map((row) => ({
          po_code: row.po_code,
          po_id: row.metadata?.po_id ?? row.po_id,
          status: arg?.action,
        }));
      if (payload.length === 0) {
        throw new Error("No POs selected");
      }
      return acknowledgePO({ data: payload });
    },
    onSuccess: (data, variable) => {
      resetAction();
      setBulkSelected([]);
      showToast({
        type: "success",
        message: variable._successMessage || `Action executed successfully`,
      });
    },
    onError: (error, variable) => {
      resetAction();
      showToast({
        type: "error",
        message:
          (variable._errorMessage || "Error performing action ") +
          error.message,
      });
    },
  });

  const resetPage = () => setCurrentPage(1);

  const _reset = () => {
    resetSearchQuery();
    resetPage();
  };

  const handleChange = (file, type = "invoice") => {
    let formData = new FormData();
    formData.append("file", file);

    if (type === "payments") {
      uploadPaymentMutate({ payload: formData });
      return;
    }
    uploadInvoiceMutate({ payload: formData });
  };

  const getFactoriesFromFactoryMap = (factoryMap) => {
    return factoryMap ? Object.keys(factoryMap).join(",") : "";
  };

  const handleFilter = (filterData) => {
    const cleanedFilter = Object.fromEntries(
      Object.entries(filterData).filter(([_, v]) =>
        Array.isArray(v) ? v.length > 0 : v !== "" && v != null
      )
    );
    setSearchParams((prev) => {
      prev.set("filterData", JSON.stringify(cleanedFilter));
      prev.set("page", 1);
      return prev;
    });
  };

  if ((isLoading && !orderList) || isUploading || isPaymentUploading || isFactoryLoading) {
    return <LoaderV2 />;
  }
  if (isError)
    return (
      <Box
        display={"flex"}
        flexDirection={"column"}
        alignItems={"center"}
        justifyContent={"center"}
        height={"100vh"}
      >
        <Typography variant="h5" color={"red"}>
          {error.message} - Error loading data
        </Typography>
        Try again later
      </Box>
    );

  const actionHandler = (invoice, actionType) => {
    setActionStatus({ ...actionStatus, [invoice.po_code]: true });
    switch (actionType) {
      case "VIEW":
        getViewPODoc(invoice);
        break;
      case "MAIL":
        sendEmail(invoice);
        break;
      case "ACKNOWLEDGE":
        if (invoice?.metadata?.po_status === "revision_requested") {
          setActionStatus({ ...actionStatus, [invoice.po_code]: false });
          // Same arm logic (and PO-code encoding) as the Orders list rows.
          const target = getPoDestination(invoice, "revision_requested");
          navigate(target, {
            state: {
              from: "dashboard",
              poId: invoice.metadata?.po_id ?? invoice.po_id,
            },
          });
          break;
        }
        acknowledgePOs({
          data: [invoice],
          action: "approve",
          _successMessage: isPendingAck
            ? "PO acknowledged. You can start uploading invoices for this PO."
            : "PO acknowledged.",
        });
        break;
      case "REJECT":
        acknowledgePOs({
          data: [invoice],
          action: "reject",
          _successMessage: "PO rejected. Livspace team has been notified. ",
        });
        break;
      case "DOWNLOAD":
        downloadPO(invoice);
        break;
      default:
        break;
    }
  };

  return (
    <>
      <ViewPOModal
        open={viewInvoice != null}
        onClose={() => {
          setViewInvoice(null);
        }}
        actionHandler={actionHandler}
        invoice={viewInvoice}
        isMobile={isMobile}
        showActions={VENDOR_ACTION_PO_STATUSES.has(
          viewInvoice?.metadata?.po_status,
        )}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "start" },
          justifyContent: "flex-start",
          pt: { xs: 1, md: 2 },
          px: { xs: 1, md: 2 },
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Order Filters */}
        {isMobile ? (
          <StatusChipStrip
            data={sidebarData}
            defaultSelect={"all_orders"}
          />
        ) : (
          <OrderFilterCard
            data={sidebarData}
            label={"ORDERS"}
            defaultSelect={"all_orders"}
          />
        )}
        <Box
          ml={{ xs: 0, md: 3 }}
          flex={1}
          sx={{ display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0, alignSelf: "stretch" }}
        >
          {/* Header Actions Start */}
          <FlexBox
            sx={{
              flexDirection: { xs: "column", md: "row" },
              justifyContent: { md: "space-between" },
              alignItems: { xs: "stretch", md: "center" },
              gap: { xs: 1, md: 0 },
              mb: 2,
            }}
          >
            <FlexBox
              gap={1}
              sx={{
                alignItems: "center",
                flexWrap: { xs: "wrap", md: "nowrap" },
              }}
            >
              <Typography fontWeight={700} fontSize="16px" lineHeight="24px">
                Order List
              </Typography>
              <Typography fontSize="12px" lineHeight="16px" color="text.secondary">
                ({orderList.total_count})
              </Typography>
              {!isPendingAck ? (
                <StatusFilter
                  filterData={dynamicFilterData}
                  applyFilter={handleFilter}
                  selectedFilters={filter}
                  cities={citiesData || []}
                  citiesLoading={isCitiesLoading}
                />
              ) : null}
            </FlexBox>
            <FlexBox
              sx={{
                flexWrap: { xs: "wrap", md: "nowrap" },
                gap: 1,
                width: { xs: "100%", md: "auto" },
              }}
            >
              <AttributeSearchInput
                value={search}
                selectedAttribute={searchBy}
                attributes={isPendingAck ? pendingAckAttributes : undefined}
                onSearch={(attr, val) => {
                  resetPage();
                  setSearchParams((prev) => {
                    prev.set("query", val);
                    prev.set("searchBy", attr);
                    return prev;
                  });
                }}
                onClear={_reset}
              />
              {!hideInvoiceUpload && (
                <>
                  <Box width={"8px"} />
                  <WithPermission permission={BOUNCER_PERMISSIONS.INVOICE_UPLOAD}>
                    <UploadFile
                      label={"Upload invoice"}
                      onUpload={handleChange}
                      filled
                    />
                  </WithPermission>
                </>
              )}
              {!isMobile && <Box width={"8px"} />}
              {!isMobile && showV1PaymentUpload && (
                <WithPermission permission={BOUNCER_PERMISSIONS.PAYMENT_UPLOAD}>
                  <UploadFile
                    label={"Upload Payments"}
                    onUpload={(file) => handleChange(file, "payments")}
                    filled
                  />
                </WithPermission>
              )}
            </FlexBox>
          </FlexBox>
          {/* Bulk approval */}
          {bulkSelectedItems.length >= 1 ? (
            <Box
              display={"flex"}
              alignItems={"center"}
              justifyContent={"flex-start"}
              gap={2}
              marginBottom={2}
            >
              <Typography fontWeight={600} fontSize="14px">
                {bulkSelectedItems.length} selected
              </Typography>

              <Button
                variant="contained"
                size="small"
                startIcon={isApprovalPending ? <CircularProgress size={12} /> : undefined}
                sx={{ backgroundColor: COLOR_CONSTANTS["primary/default"], textTransform: "none", px: "15px", py: "8px" }}
                disabled={isApprovalPending}
                onClick={() => {
                  acknowledgePOs({
                    data: [...bulkSelectedItems],
                    action: "approve",
                    _successMessage: `${bulkSelectedItems.length} PO(s) acknowledged successfully`,
                  });
                }}
              >
                Acknowledge POs
              </Button>
              <Button
                variant="text"
                size="small"
                disabled={isApprovalPending}
                onClick={() => setBulkSelected([])}
                sx={{ textTransform: "none" }}
              >
                Clear
              </Button>
            </Box>
          ) : null}
          {/* Header Actions End */}

          {/* Refetch indicator — only shown when refreshing with existing data */}
          {isFetching && orderList && (
            <LinearProgress sx={{ mb: 1, borderRadius: "4px" }} />
          )}

          <Box sx={{ flex: { xs: 1, md: "none" }, minHeight: 0, maxHeight: { md: "calc(100vh - 340px)" }, width: "100%", overflowY: "auto", overflowX: "auto", WebkitOverflowScrolling: "touch", bgcolor: "background.default", borderRadius: "8px" }}>
            <Box sx={{ width: "100%", bgcolor: "background.paper" }}>
              {/* Data display */}
              <Box sx={{ opacity: isFetching && orderList ? 0.5 : 1, transition: "opacity 0.15s" }}>
                <DataTable
                  data={orderList.po_list || []}
                  actionHandler={actionHandler}
                  isActionLoading={actionStatus}
                  bulkSelection={bulkSelection}
                  selectInvoice={setSelectedInvoice}
                  orderStatus={orderStatus}
                  activeFilterStatuses={activeFilterStatuses}
                  hideInvoiceExpand={hideInvoiceExpand}
                  isMobile={isMobile}
                />
              </Box>
            </Box>
          </Box>

          {/* Pagination */}
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "center", md: "flex-end" },
              marginLeft: { md: "auto" },
              py: 2,
            }}
          >
            {isMobile ? (
              <MobilePagination
                page={parseInt(page, 10)}
                totalPages={Math.ceil(orderList.total_count / PAGE_SIZE)}
                onPageChange={setCurrentPage}
                totalItems={orderList.total_count}
                pageSize={PAGE_SIZE}
              />
            ) : (
              <Pagination
                count={Math.ceil(orderList.total_count / PAGE_SIZE)}
                variant="outlined"
                shape="rounded"
                page={parseInt(page, 10)}
                onChange={(_, page) => {
                  setCurrentPage(page);
                }}
                sx={{
                  "& .MuiPaginationItem-root.Mui-selected": {
                    color: COLOR_CONSTANTS["on-primary/variant"],
                    backgroundColor: COLOR_CONSTANTS["primary/variant"],
                    borderColor: COLOR_CONSTANTS["on-primary/variant"],
                  },
                }}
              />
            )}
          </Box>
          {isMobile && showV1PaymentUpload && (
            <WithPermission permission={BOUNCER_PERMISSIONS.PAYMENT_UPLOAD}>
              <UploadFile
                label={"Upload Payments"}
                onUpload={(file) => handleChange(file, "payments")}
                iconOnly
                sx={MOBILE_FAB_SX}
              />
            </WithPermission>
          )}
          <Box sx={{ flex: 1, display: { xs: "none", md: "block" } }} />
        </Box>
      </Box>
    </>
  );
}

export default OrderDetails;
